import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import {
  FILE_FOLDERS,
  fileMatchesFolder,
  isImageFile,
  LANG_FLAGS,
  LANG_LABELS,
  mapFileRow,
  TRANSLATE_LANG_OPTIONS,
  TRANSLATION_STATUS,
} from './adminToolShared'
import { RsIcon, IconLabel, ModalClose } from '../../../components/icons'
import { useConfirm } from '../../../contexts/ConfirmContext'
import TranslationViewer from '../../../components/TranslationViewer'

import SharePackageModal from '../../../components/Files/SharePackageModal'
import DownloadCodeModal from '../../../components/Files/DownloadCodeModal'

const FILE_TABS = ['Project Folders', 'All Files', 'Shared with Me', 'Shared Out', 'Recent', 'Translated', 'Confidential']

function isUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(value || ''))
}

function formatShareStatus(status) {
  if (status === 'active') return 'Active'
  if (status === 'expired') return 'Expired'
  if (status === 'revoked') return 'Closed'
  return status
}

function FileTranslationBadges({ file, onView }) {
  const langs = Object.keys(file.translations || {})
  if (!langs.length) {
    return <span className="admin-file-tr-empty">—</span>
  }
  return (
    <div className="admin-file-tr-badges">
      {langs.map((lang) => {
        const status = file.translations[lang]
        return (
          <button
            key={lang}
            type="button"
            className={`admin-file-tr-badge ${TRANSLATION_STATUS[status]?.color || 'pending'}`}
            title={`${LANG_LABELS[lang] || lang} — ${TRANSLATION_STATUS[status]?.label || status}`}
            onClick={(e) => { e.stopPropagation(); onView?.(file, lang) }}
          >
            {LANG_FLAGS[lang] || lang}
          </button>
        )
      })}
      <button type="button" className="admin-file-tr-view" onClick={(e) => { e.stopPropagation(); onView?.(file) }}>
        View translation
      </button>
    </div>
  )
}

function SharedFileActions({ file, onDownload, onPreview }) {
  return (
    <div className="admin-file-actions admin-row-actions" onClick={(e) => e.stopPropagation()}>
      {isImageFile(file) && (
        <button type="button" className="admin-row-action-btn" onClick={() => onPreview(file)} title="Preview">
          <RsIcon name="expand" size="xs" />
          <span>Preview</span>
        </button>
      )}
      <button type="button" className="admin-row-action-btn" onClick={() => onDownload(file)} title="Download">
        <RsIcon name="download" size="xs" />
        <span>Download</span>
      </button>
    </div>
  )
}

function FileActions({ file, onDownload, onPreview, onTranslate, onDelete, deleting }) {
  return (
    <div className="admin-file-actions admin-row-actions" data-file-id={file.id} onClick={(e) => e.stopPropagation()}>
      {isImageFile(file) && (
        <button type="button" className="admin-row-action-btn" data-testid="file-preview-btn" onClick={() => onPreview(file)} title="Preview">
          <RsIcon name="expand" size="xs" />
          <span>Preview</span>
        </button>
      )}
      <button type="button" className="admin-row-action-btn" data-testid="file-download-btn" onClick={() => onDownload(file)} title="Download">
        <RsIcon name="download" size="xs" />
        <span>Download</span>
      </button>
      <button type="button" className="admin-row-action-btn" data-testid="file-translate-btn" onClick={() => onTranslate(file)} title="Translate">
        <RsIcon name="translate" size="xs" />
        <span>Translate</span>
      </button>
      <button
        type="button"
        className="admin-row-action-btn danger"
        data-testid="file-delete-btn"
        onClick={() => onDelete(file)}
        disabled={deleting}
        title="Delete"
      >
        <RsIcon name="trash" size="xs" />
        <span>Delete</span>
      </button>
    </div>
  )
}

export default function AdminFilesView() {
  const confirm = useConfirm()
  const [searchParams, setSearchParams] = useSearchParams()
  const shareOpenedRef = useRef(null)
  const [files, setFiles] = useState([])
  const [deals, setDeals] = useState([])
  const [tab, setTab] = useState('Project Folders')
  const [view, setView] = useState('grid')
  const [search, setSearch] = useState('')
  const [showUpload, setShowUpload] = useState(false)
  const [showTranslate, setShowTranslate] = useState(false)
  const [viewTranslations, setViewTranslations] = useState(null)
  const [viewTranslationLang, setViewTranslationLang] = useState(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [translateLangs, setTranslateLangs] = useState(['en', 'zh'])
  const [folderFilter, setFolderFilter] = useState(null)
  const [storage, setStorage] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [translating, setTranslating] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadDealId, setUploadDealId] = useState('')
  const [uploadFolderId, setUploadFolderId] = useState('')
  const [projectFolders, setProjectFolders] = useState([])
  const [receivedShares, setReceivedShares] = useState([])
  const [outgoingShares, setOutgoingShares] = useState([])
  const [activeShareGrant, setActiveShareGrant] = useState(null)
  const [sharedFiles, setSharedFiles] = useState([])
  const [shareModalFolder, setShareModalFolder] = useState(null)
  const [downloadCodeFile, setDownloadCodeFile] = useState(null)
  const [newFolderName, setNewFolderName] = useState('')
  const [creatingFolder, setCreatingFolder] = useState(false)
  const [showNewFolder, setShowNewFolder] = useState(false)
  const [previewImage, setPreviewImage] = useState(null)
  const fileInputRef = useRef(null)
  const previewUrlRef = useRef(null)

  const loadStorage = useCallback(async () => {
    try {
      const res = await axios.get('/api/files/storage-usage')
      setStorage(res.data)
    } catch {
      setStorage(null)
    }
  }, [])

  const loadProjectFolders = useCallback(async () => {
    try {
      const res = await axios.get('/api/file-folders')
      setProjectFolders(res.data?.folders || [])
    } catch {
      setProjectFolders([])
    }
  }, [])

  const loadReceivedShares = useCallback(async () => {
    try {
      const res = await axios.get('/api/share-packages/received')
      setReceivedShares(res.data?.packages || [])
    } catch {
      setReceivedShares([])
    }
  }, [])

  const loadOutgoingShares = useCallback(async () => {
    try {
      const res = await axios.get('/api/share-packages/sent')
      setOutgoingShares(res.data?.packages || [])
    } catch {
      setOutgoingShares([])
    }
  }, [])

  const openShareGrant = useCallback(async (grantMeta) => {
    if (!grantMeta || grantMeta.status !== 'active') {
      if (grantMeta?.status === 'expired') toast.error('This share has expired')
      else if (grantMeta?.status === 'revoked') toast.error('This share has been closed')
      return
    }
    try {
      const res = await axios.get(`/api/share-packages/grants/${grantMeta.grantId}/files`)
      setActiveShareGrant(res.data.grant)
      setSharedFiles((res.data.files || []).map(mapFileRow))
      setFolderFilter(null)
      setTab('Shared with Me')
      setView('list')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to open shared folder')
    }
  }, [])

  const loadFiles = useCallback(async () => {
    try {
      const res = await axios.get('/api/files')
      setFiles(res.data.map(mapFileRow))
    } catch {
      toast.error('Failed to load files')
    }
  }, [])

  const handleSharedPreview = useCallback(async (file) => {
    if (!activeShareGrant?.grantId || !isImageFile(file)) return
    try {
      const res = await axios.get(`/api/files/download/${file.id}?grantId=${activeShareGrant.grantId}`, {
        responseType: 'blob',
      })
      if (previewUrlRef.current) window.URL.revokeObjectURL(previewUrlRef.current)
      const blobUrl = window.URL.createObjectURL(res.data)
      previewUrlRef.current = blobUrl
      setPreviewImage({ file, url: blobUrl })
    } catch {
      toast.error('Preview failed')
    }
  }, [activeShareGrant?.grantId])

  const handleDownload = useCallback(async (file) => {
    if (activeShareGrant?.grantId) {
      setDownloadCodeFile(file)
      return false
    }
    try {
      const url = file.url || `/api/files/download/${file.id}`
      const res = await axios.get(url, { responseType: 'blob' })
      const blobUrl = window.URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = file.name || 'download'
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(blobUrl)
      toast.success(`Downloaded ${file.name}`)
      return true
    } catch {
      toast.error('Download failed')
      return false
    }
  }, [activeShareGrant?.grantId])

  const handlePreview = useCallback(async (file) => {
    if (activeShareGrant?.grantId) {
      return handleSharedPreview(file)
    }
    if (!isImageFile(file)) return
    try {
      const url = file.url || `/api/files/download/${file.id}`
      const res = await axios.get(url, { responseType: 'blob' })
      if (previewUrlRef.current) {
        window.URL.revokeObjectURL(previewUrlRef.current)
      }
      const blobUrl = window.URL.createObjectURL(res.data)
      previewUrlRef.current = blobUrl
      setPreviewImage({ file, url: blobUrl })
    } catch {
      toast.error('Preview failed')
    }
  }, [activeShareGrant?.grantId, handleSharedPreview])

  const closePreview = useCallback(() => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current)
      previewUrlRef.current = null
    }
    setPreviewImage(null)
  }, [])

  useEffect(() => {
    loadFiles()
    loadStorage()
    loadProjectFolders()
    loadReceivedShares()
    loadOutgoingShares()
    axios.get('/api/deals/my-deals').then((r) => setDeals(r.data || [])).catch(() => {})
  }, [loadFiles, loadStorage, loadProjectFolders, loadReceivedShares, loadOutgoingShares])

  useEffect(() => {
    const grantId = searchParams.get('shared')
    if (!grantId || !receivedShares.length) return
    const match = receivedShares.find((s) => s.grantId === grantId)
    if (match) {
      openShareGrant(match)
      const next = new URLSearchParams(searchParams)
      next.delete('shared')
      setSearchParams(next, { replace: true })
    }
  }, [receivedShares, searchParams, setSearchParams, openShareGrant])

  useEffect(() => () => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current)
    }
  }, [])

  useEffect(() => {
    const fileId = searchParams.get('file')
    if (!fileId || !files.length) return
    if (shareOpenedRef.current === fileId) return

    const file = files.find((f) => f.id === fileId)
    if (!file) {
      toast.error('File not found or you do not have access')
      const next = new URLSearchParams(searchParams)
      next.delete('file')
      setSearchParams(next, { replace: true })
      return
    }

    shareOpenedRef.current = fileId
    setTab('All Files')
    setView('list')
    const openAction = isImageFile(file) ? handlePreview(file) : handleDownload(file)
    openAction.finally(() => {
      const next = new URLSearchParams(searchParams)
      next.delete('file')
      setSearchParams(next, { replace: true })
    })
  }, [files, searchParams, setSearchParams, handleDownload, handlePreview])

  const filtered = useMemo(() => {
    let list = [...files]
    if (search) {
      const s = search.toLowerCase()
      list = list.filter((f) =>
        f.name.toLowerCase().includes(s) || String(f.deal || '').toLowerCase().includes(s)
      )
    }
    if (folderFilter) {
      if (isUuid(folderFilter)) {
        list = list.filter((f) => f.folderId === folderFilter)
      } else {
        list = list.filter((f) => fileMatchesFolder(f.name, folderFilter))
      }
    }
    if (tab === 'Shared') list = list.filter((f) => f.deal)
    if (tab === 'Recent') list.sort((a, b) => (b.uploadedAt || '').localeCompare(a.uploadedAt || ''))
    if (tab === 'Translated') list = list.filter((f) => Object.keys(f.translations || {}).length > 0)
    if (tab === 'Confidential') list = list.filter((f) => f.isConfidential)
    return list
  }, [files, search, folderFilter, tab])

  const totalBytes = storage?.usedBytes ?? files.reduce((s, f) => s + (f.sizeBytes || 0), 0)
  const limitGb = storage?.limitGb ?? 50
  const usedGb = totalBytes / (1024 ** 3)
  const usedLabel = usedGb >= 0.1
    ? `${usedGb.toFixed(1)} GB`
    : totalBytes >= 1024 * 1024
      ? `${(totalBytes / (1024 ** 2)).toFixed(1)} MB`
      : `${(totalBytes / 1024).toFixed(1)} KB`
  const pct = storage?.percentUsed ?? Math.min(100, Math.round((totalBytes / (limitGb * 1024 ** 3)) * 100))

  const openTranslationViewer = useCallback((file, lang) => {
    setViewTranslations(file)
    setViewTranslationLang(lang || null)
  }, [])

  const closeTranslationViewer = useCallback(() => {
    setViewTranslations(null)
    setViewTranslationLang(null)
  }, [])

  const openTranslate = (file) => {
    setSelectedFile(file)
    setShowTranslate(true)
  }

  const handleDelete = async (file) => {
    const ok = await confirm({
      title: 'Delete file?',
      message: `Delete "${file.name}"? This cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    })
    if (!ok) return
    setDeletingId(file.id)
    try {
      await axios.delete(`/api/files/${file.id}`)
      toast.success('File deleted')
      if (viewTranslations?.id === file.id) closeTranslationViewer()
      await loadFiles()
      await loadStorage()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed')
    } finally {
      setDeletingId(null)
    }
  }

  const handleTranslate = async () => {
    if (!selectedFile) return
    setTranslating(true)
    try {
      const res = await axios.post('/api/files/translate', {
        fileId: selectedFile.id,
        targetLanguages: translateLangs,
      })
      const { translations: texts, translationMeta, okCount, source, sourceCharCount } = res.data
      const okLangs = Object.entries(translationMeta || {})
        .filter(([, s]) => s === 'ok')
        .map(([l]) => LANG_LABELS[l] || l)
      const failLangs = Object.entries(translationMeta || {})
        .filter(([, s]) => s === 'failed')
        .map(([l]) => LANG_LABELS[l] || l)

      if (okCount === 0) {
        toast.error(res.data.message || 'Translation failed', { duration: 6000 })
      } else if (failLangs.length) {
        toast.success(`Translated: ${okLangs.join(', ')}; failed: ${failLangs.join(', ')}`)
      } else {
        const srcNote =
          source === 'pdf_content'
            ? `PDF body (~${sourceCharCount || '?'} chars)`
            : source === 'word_content'
              ? `Word body (~${sourceCharCount || '?'} chars)`
            : source === 'file_content'
              ? 'document body'
              : 'filename summary'
        toast.success(`Translated to ${okLangs.join(', ')} (${srcNote})`)
      }

      await loadFiles()
      setShowTranslate(false)
      if (okCount > 0 && selectedFile) {
        const merged = { ...selectedFile }
        merged.translationTexts = texts
        merged.translations = {}
        Object.entries(texts || {}).forEach(([lang, text]) => {
          merged.translations[lang] = /^【/.test(String(text).trim()) ? 'failed' : 'ready'
        })
        openTranslationViewer(
          merged,
          Object.entries(translationMeta || {}).find(([, s]) => s === 'ok')?.[0] || null,
        )
      }
      setSelectedFile(null)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Translation failed')
    } finally {
      setTranslating(false)
    }
  }

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    setUploadProgress(0)
    const formData = new FormData()
    formData.append('file', file)
    if (uploadDealId) formData.append('dealId', uploadDealId)
    const folderForUpload = uploadFolderId || (isUuid(folderFilter) ? folderFilter : '')
    if (folderForUpload) formData.append('folderId', folderForUpload)
    formData.append('isConfidential', 'false')
    try {
      await axios.post('/api/files/upload', formData, {
        onUploadProgress: (ev) => {
          if (ev.total) {
            setUploadProgress(Math.round((ev.loaded * 100) / ev.total))
          }
        },
      })
      toast.success('File uploaded successfully!')
      setShowUpload(false)
      setUploadProgress(0)
      setTab('All Files')
      setView('list')
      if (!folderForUpload) setFolderFilter(null)
      await loadFiles()
      await loadProjectFolders()
      await loadStorage()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed')
      setUploadProgress(0)
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const openProjectFolder = (folder) => {
    setActiveShareGrant(null)
    setSharedFiles([])
    setFolderFilter(folder.id)
    setTab('All Files')
    setView('list')
  }

  const handleCreateFolder = async (e) => {
    e.preventDefault()
    const name = newFolderName.trim()
    if (!name) return
    setCreatingFolder(true)
    try {
      await axios.post('/api/file-folders', { name })
      toast.success('Folder created')
      setNewFolderName('')
      setShowNewFolder(false)
      await loadProjectFolders()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create folder')
    } finally {
      setCreatingFolder(false)
    }
  }

  const handleRevokeShare = async (packageId) => {
    const ok = await confirm({
      title: 'Close this share?',
      message: 'Recipients will no longer be able to view or download these files.',
      confirmText: 'Close Share',
      variant: 'danger',
    })
    if (!ok) return
    try {
      await axios.post(`/api/share-packages/${packageId}/revoke`)
      toast.success('Share closed')
      await loadOutgoingShares()
      await loadReceivedShares()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to close share')
    }
  }

  const folderCount = (folderId) => {
    if (isUuid(folderId)) {
      const folder = projectFolders.find((f) => f.id === folderId)
      return folder?.fileCount ?? files.filter((f) => f.folderId === folderId).length
    }
    return files.filter((f) => fileMatchesFolder(f.name, folderId)).length
  }

  const currentProjectFolder = isUuid(folderFilter)
    ? projectFolders.find((f) => f.id === folderFilter)
    : null

  const breadcrumbLabel = activeShareGrant
    ? (activeShareGrant.title || activeShareGrant.folderName)
    : currentProjectFolder?.name
      || (folderFilter ? FILE_FOLDERS.find((f) => f.id === folderFilter)?.name : null)
      || (tab === 'Project Folders' ? 'All Folders' : tab)

  const switchTab = (nextTab) => {
    setTab(nextTab)
    if (nextTab === 'Project Folders') {
      setFolderFilter(null)
      setActiveShareGrant(null)
      setSharedFiles([])
    } else if (nextTab === 'Shared with Me') {
      setFolderFilter(null)
      if (!activeShareGrant) {
        setSharedFiles([])
      }
    } else if (nextTab === 'Shared Out') {
      setFolderFilter(null)
      setActiveShareGrant(null)
      setSharedFiles([])
    } else {
      setActiveShareGrant(null)
      setSharedFiles([])
    }
  }

  const goFilesHome = () => {
    setTab('Project Folders')
    setFolderFilter(null)
    setActiveShareGrant(null)
    setSharedFiles([])
  }

  const goBackFromShare = () => {
    setActiveShareGrant(null)
    setSharedFiles([])
    setTab('Shared with Me')
  }

  const formatShareDate = (value) => {
    if (!value) return '—'
    try {
      return new Date(value).toLocaleDateString()
    } catch {
      return value
    }
  }

  const isSharedView = !!activeShareGrant
  const displayFiles = activeShareGrant ? sharedFiles : filtered
  const showProjectFolderGrid = tab === 'Project Folders' && view === 'grid' && !activeShareGrant
  const showSharedWithMe = tab === 'Shared with Me' && !activeShareGrant
  const showSharedOut = tab === 'Shared Out'
  const showFileGrid = !showProjectFolderGrid && !showSharedWithMe && !showSharedOut && view === 'grid'
  const showFileTable = !showProjectFolderGrid && !showSharedWithMe && !showSharedOut && !showFileGrid

  return (
    <section className="admin-tool-files">
      <header className="admin-tool-topbar">
        <div className="admin-tool-topbar-left">
          <span className="admin-tool-topbar-title">Files</span>
          <span className="admin-tool-topbar-sub">Secure storage · multi-language translation (DeepSeek)</span>
        </div>
      </header>

      <div className="admin-files-body">
        <div className="admin-files-storage">
          <div className="admin-files-storage-info">
            <div className="admin-files-storage-label">Storage: {usedLabel} / {limitGb} GB used</div>
            <div className="admin-files-storage-track">
              <div className="admin-files-storage-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="admin-files-storage-detail">{(Math.max(0, limitGb - usedGb)).toFixed(1)} GB available</div>
          </div>
        </div>

        <div className="admin-files-tabs">
          {FILE_TABS.map((t) => (
            <button
              key={t}
              type="button"
              className={`admin-files-tab${tab === t ? ' active' : ''}`}
              onClick={() => switchTab(t)}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="admin-files-toolbar">
          <div className="admin-tool-search">
            <RsIcon name="search" size="sm" aria-hidden />
            <input placeholder="Search files, folders..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="admin-files-view-toggle">
            <button type="button" className={view === 'grid' ? 'active' : ''} onClick={() => setView('grid')} aria-label="Grid view">
              <RsIcon name="gridView" size="sm" />
            </button>
            <button type="button" className={view === 'list' ? 'active' : ''} onClick={() => setView('list')} aria-label="List view">
              <RsIcon name="listView" size="sm" />
            </button>
          </div>
          {tab === 'Project Folders' && !activeShareGrant && (
            <button type="button" className="admin-btn secondary" onClick={() => setShowNewFolder(true)}>
              <IconLabel name="folder" size="xs">New Folder</IconLabel>
            </button>
          )}
          {currentProjectFolder && (
            <button type="button" className="admin-btn secondary" onClick={() => setShareModalFolder(currentProjectFolder)}>
              <IconLabel name="share" size="xs">Share</IconLabel>
            </button>
          )}
          {!showSharedWithMe && !showSharedOut && (
            <button type="button" className="admin-btn primary" onClick={() => setShowUpload(true)}>
              <IconLabel name="upload" size="xs">Upload</IconLabel>
            </button>
          )}
        </div>

        <div className="admin-files-breadcrumb">
          <button type="button" className="admin-files-crumb-btn" onClick={goFilesHome}>Files</button>
          {activeShareGrant && (
            <>
              <span>›</span>
              <button type="button" className="admin-files-crumb-btn" onClick={goBackFromShare}>Shared with Me</button>
            </>
          )}
          {currentProjectFolder && !activeShareGrant && (
            <>
              <span>›</span>
              <button type="button" className="admin-files-crumb-btn" onClick={() => { setFolderFilter(null); setTab('Project Folders') }}>Project Folders</button>
            </>
          )}
          <span>›</span>
          <span className="current">{breadcrumbLabel}</span>
        </div>

        {showNewFolder && (
          <div className="admin-modal-overlay show" onClick={() => !creatingFolder && setShowNewFolder(false)}>
            <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
              <div className="admin-modal-header">
                <h3>New Project Folder</h3>
                <ModalClose onClick={() => setShowNewFolder(false)} disabled={creatingFolder} />
              </div>
              <form className="admin-modal-body" onSubmit={handleCreateFolder}>
                <label className="field">
                  <span>Folder name</span>
                  <input
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="e.g. Q2 Data Room"
                    autoFocus
                    required
                  />
                </label>
                <div className="admin-modal-footer" style={{ padding: 0, marginTop: 16 }}>
                  <button type="button" className="admin-btn secondary" disabled={creatingFolder} onClick={() => setShowNewFolder(false)}>Cancel</button>
                  <button type="submit" className="admin-btn primary" disabled={creatingFolder}>
                    {creatingFolder ? 'Creating…' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showProjectFolderGrid && (
          <div className="admin-folder-grid">
            {projectFolders.length === 0 ? (
              <p className="admin-files-empty">No project folders yet. Create one to organize files for sharing.</p>
            ) : projectFolders.map((folder) => (
              <article key={folder.id} className="admin-folder-card admin-project-folder-card">
                <button type="button" className="admin-project-folder-open" onClick={() => openProjectFolder(folder)}>
                  <div className="admin-folder-icon share">
                    <RsIcon name="folder" size="md" />
                  </div>
                  <div className="admin-folder-name">{folder.name}</div>
                  <div className="admin-folder-count">{folder.fileCount ?? folderCount(folder.id)} files</div>
                </button>
                <div className="admin-folder-card-actions">
                  <button type="button" className="admin-btn secondary sm" onClick={() => openProjectFolder(folder)}>Open</button>
                  <button type="button" className="admin-btn primary sm" onClick={() => setShareModalFolder(folder)}>Share</button>
                </div>
              </article>
            ))}
          </div>
        )}

        {showSharedWithMe && (
          <div className="admin-share-list">
            {receivedShares.length === 0 ? (
              <p className="admin-files-empty">No shared folders yet. Shares from others will appear here after you log in.</p>
            ) : receivedShares.map((share) => (
              <article
                key={share.grantId}
                className={`admin-share-grant-card ${share.status}`}
              >
                <div className="admin-share-grant-main">
                  <div className="admin-share-grant-title">{share.title || share.folderName}</div>
                  <div className="admin-share-grant-meta">
                    From {share.ownerName}{share.ownerCompany ? ` · ${share.ownerCompany}` : ''}
                    {' · '}{share.fileCount} files
                    {' · '}Until {formatShareDate(share.expiresAt)}
                  </div>
                </div>
                <div className="admin-share-grant-side">
                  <span className={`admin-share-status ${share.status}`}>{formatShareStatus(share.status)}</span>
                  <button
                    type="button"
                    className="admin-btn primary sm admin-share-grant-open"
                    disabled={share.status !== 'active'}
                    onClick={() => openShareGrant(share)}
                  >
                    Open
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}

        {showSharedOut && (
          <div className="admin-share-list">
            {outgoingShares.length === 0 ? (
              <p className="admin-files-empty">You have not shared any project folders yet.</p>
            ) : outgoingShares.map((share) => (
              <article
                key={share.id}
                className={`admin-share-grant-card ${share.status}`}
              >
                <div className="admin-share-grant-main">
                  <div className="admin-share-grant-title">{share.title || share.folderName}</div>
                  <div className="admin-share-grant-meta">
                    {share.folderName}
                    {' · '}{share.fileCount} files
                    {' · '}{share.recipientCount} recipients
                    {' · '}Until {formatShareDate(share.expiresAt)}
                  </div>
                </div>
                <div className="admin-share-grant-side">
                  <span className={`admin-share-status ${share.status}`}>{formatShareStatus(share.status)}</span>
                  {share.status === 'active' && (
                    <button type="button" className="admin-btn danger sm" onClick={() => handleRevokeShare(share.id)}>
                      Close
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        {isSharedView && (
          <div className="admin-share-active-banner">
            <RsIcon name="info" size="sm" />
            <span>
              Shared folder — viewing is open; downloads require your email-bound access code.
              {!activeShareGrant.allowDownload && ' Downloads are disabled for this share.'}
            </span>
          </div>
        )}

        {showFileGrid && (
          <div className="admin-file-card-grid">
            {displayFiles.length === 0 ? (
              <p className="admin-files-empty">No files found</p>
            ) : displayFiles.map((f) => (
              <article key={f.id} className="admin-file-card">
                <div className="admin-file-card-head">
                  <div className={`admin-file-type-icon ${f.type}`}>
                    <RsIcon name={f.type === 'png' ? 'image' : 'fileText'} size="md" />
                  </div>
                  <div className="admin-file-card-meta">
                    <button
                      type="button"
                      className={`admin-file-name${isImageFile(f) ? ' admin-file-name-previewable' : ''}`}
                      title={f.name}
                      onClick={isImageFile(f) ? () => handlePreview(f) : undefined}
                    >
                      {f.name}
                    </button>
                    <div className="admin-file-card-sub">{f.size} · {f.uploadedAt}</div>
                  </div>
                  {f.isConfidential && <span className="admin-file-tag confidential">Confidential</span>}
                </div>
                {!isSharedView && f.deal && <p className="admin-file-card-deal">Deal: {f.deal}</p>}
                {!isSharedView && <FileTranslationBadges file={f} onView={openTranslationViewer} />}
                {isSharedView ? (
                  <SharedFileActions file={f} onDownload={handleDownload} onPreview={handlePreview} />
                ) : (
                  <FileActions file={f} onDownload={handleDownload} onPreview={handlePreview} onTranslate={openTranslate} onDelete={handleDelete} deleting={deletingId === f.id} />
                )}
              </article>
            ))}
          </div>
        )}

        {showFileTable && (
          <div className={`admin-file-table${isSharedView ? ' admin-file-table-shared' : ''}`}>
            <div className="admin-file-table-header">
              <span /><span>Name</span><span>Type</span><span>Size</span><span>Modified</span>
              {!isSharedView && <span>Translations</span>}
              <span>Actions</span>
            </div>
            {displayFiles.length === 0 ? (
              <p className="admin-files-empty">No files found</p>
            ) : displayFiles.map((f) => (
              <div key={f.id} className="admin-file-row">
                <div className={`admin-file-type-icon ${f.type}`}>
                  <RsIcon name={f.type === 'png' ? 'image' : 'fileText'} size="md" />
                </div>
                <div className="admin-file-name-cell">
                  <button
                    type="button"
                    className={`admin-file-name${isImageFile(f) ? ' admin-file-name-previewable' : ''}`}
                    title={f.name}
                    onClick={isImageFile(f) ? () => handlePreview(f) : undefined}
                  >
                    {f.name}
                    {f.isConfidential && <span className="admin-file-tag confidential">Confidential</span>}
                  </button>
                </div>
                <span>{f.type.toUpperCase()}</span>
                <span>{f.size}</span>
                <span className="admin-file-col admin-file-col-modified">{f.uploadedAt}</span>
                {!isSharedView && (
                  <div className="admin-file-col admin-file-col-translations">
                    <FileTranslationBadges file={f} onView={openTranslationViewer} />
                  </div>
                )}
                <div className="admin-file-col admin-file-col-actions">
                  {isSharedView ? (
                    <SharedFileActions file={f} onDownload={handleDownload} onPreview={handlePreview} />
                  ) : (
                    <FileActions file={f} onDownload={handleDownload} onPreview={handlePreview} onTranslate={openTranslate} onDelete={handleDelete} deleting={deletingId === f.id} />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showUpload && (
        <div className="admin-modal-overlay show" onClick={() => !uploading && setShowUpload(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Upload File</h3>
              <ModalClose onClick={() => setShowUpload(false)} disabled={uploading} />
            </div>
            <div className="admin-modal-body">
              {projectFolders.length > 0 && (
                <div className="field">
                  <label htmlFor="upload-folder">Project folder (optional)</label>
                  <select id="upload-folder" value={uploadFolderId} onChange={(e) => setUploadFolderId(e.target.value)}>
                    <option value="">None</option>
                    {projectFolders.map((f) => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {deals.length > 0 && (
                <div className="field">
                  <label htmlFor="upload-deal">Link to Deal (optional)</label>
                  <select id="upload-deal" value={uploadDealId} onChange={(e) => setUploadDealId(e.target.value)}>
                    <option value="">None</option>
                    {deals.map((d) => (
                      <option key={d.id} value={d.id}>{d.title}</option>
                    ))}
                  </select>
                </div>
              )}
              <label className="admin-upload-zone" style={{ display: 'block', cursor: uploading ? 'wait' : 'pointer' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handleUpload}
                  disabled={uploading}
                />
                <div className="uz-icon"><RsIcon name="folder" size="lg" /></div>
                <p>{uploading ? `Uploading… ${uploadProgress}%` : 'Click to browse or drop a file here'}</p>
                <p style={{ fontSize: 12, color: '#909399', marginTop: 8 }}>PDF, Office, images, archives, and other common formats (max 100MB)</p>
              </label>
              {uploading && (
                <div className="admin-upload-progress">
                  <div className="admin-upload-progress-bar" style={{ width: `${uploadProgress}%` }} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {previewImage && (
        <div className="admin-modal-overlay show" onClick={closePreview}>
          <div className="admin-modal admin-modal-lg admin-file-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <div>
                <h3>Image Preview</h3>
                <p className="admin-modal-subtitle">{previewImage.file.name}</p>
              </div>
              <ModalClose onClick={closePreview} />
            </div>
            <div className="admin-modal-body admin-file-preview-body">
              <img src={previewImage.url} alt={previewImage.file.name} className="admin-file-preview-img" />
            </div>
            <div className="admin-modal-footer">
              <button type="button" className="admin-btn secondary" onClick={closePreview}>Close</button>
              <button type="button" className="admin-btn primary" onClick={() => handleDownload(previewImage.file)}>
                <IconLabel name="download" size="xs">Download</IconLabel>
              </button>
            </div>
          </div>
        </div>
      )}

      {viewTranslations && (
        <TranslationViewer
          file={viewTranslations}
          initialLang={viewTranslationLang}
          onClose={closeTranslationViewer}
        />
      )}

      {showTranslate && selectedFile && (
        <div className="admin-modal-overlay show" onClick={() => !translating && (setShowTranslate(false), setSelectedFile(null))}>
          <div className="admin-modal admin-modal-translate admin-modal-scroll" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Translate Document</h3>
              <ModalClose onClick={() => { setShowTranslate(false); setSelectedFile(null) }} disabled={translating} />
            </div>
            <div className="admin-modal-body">
              <p className="admin-modal-subtitle">{selectedFile.name}</p>
              <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Select target languages ({TRANSLATE_LANG_OPTIONS.length} supported):</p>
              <div className="admin-translate-lang-list">
                {TRANSLATE_LANG_OPTIONS.map(({ code, label, flag }) => (
                  <label key={code} className="admin-translate-lang-item">
                    <span className="admin-translate-lang-item-label">
                      <span className="admin-translate-lang-flag" aria-hidden>{flag}</span>
                      <span className="admin-translate-lang-name">{label}</span>
                    </span>
                    <input
                      type="checkbox"
                      checked={translateLangs.includes(code)}
                      onChange={(e) => {
                        if (e.target.checked) setTranslateLangs((prev) => [...prev, code])
                        else setTranslateLangs((prev) => prev.filter((l) => l !== code))
                      }}
                    />
                  </label>
                ))}
              </div>
              <div className="admin-settings-alert info" style={{ marginTop: 16 }}>
                <RsIcon name="info" size="md" />
                <span><strong>Powered by DeepSeek</strong> — PDF body parsing and multi-language translation; scanned PDFs require OCR</span>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button type="button" className="admin-btn secondary" onClick={() => { setShowTranslate(false); setSelectedFile(null) }} disabled={translating}>Cancel</button>
              <button type="button" className="admin-btn primary" onClick={handleTranslate} disabled={translateLangs.length === 0 || translating}>
                {translating ? 'Translating…' : 'Start Translation'}
              </button>
            </div>
          </div>
        </div>
      )}

      <SharePackageModal
        open={!!shareModalFolder}
        folder={shareModalFolder}
        onClose={() => setShareModalFolder(null)}
        onCreated={() => {
          loadOutgoingShares()
          loadReceivedShares()
        }}
      />

      <DownloadCodeModal
        open={!!downloadCodeFile}
        file={downloadCodeFile}
        grantId={activeShareGrant?.grantId}
        onClose={() => setDownloadCodeFile(null)}
      />
    </section>
  )
}
